import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyTotp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [totpPendingToken, setTotpPendingToken] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);

  const returnTo = location.state?.returnTo || '/';

  const routeForUser = (user) => {
    if (user.role === 'admin') return '/admin';
    if (user.role === 'artist') return '/artist-dashboard';
    if (user.role === 'agent') return '/agent-dashboard';
    return returnTo;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_totp) {
        setTotpPendingToken(result.totp_pending_token);
        setTotpCode('');
        toast.info('Enter your authenticator app code to finish signing in');
        return;
      }
      toast.success(`Welcome back, ${result.name}!`);
      navigate(routeForUser(result));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    const cleaned = totpCode.trim();
    if (!cleaned) {
      toast.error('Please enter your 2FA code');
      return;
    }
    setTotpLoading(true);
    try {
      const user = await verifyTotp(totpPendingToken, cleaned);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(routeForUser(user));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleBackToPassword = () => {
    setTotpPendingToken(null);
    setTotpCode('');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div {...fadeInUp} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="Tune4Tune" className="h-12 w-auto" />
            <span className="font-heading font-bold text-2xl">Tune4Tune</span>
          </Link>

          {!totpPendingToken ? (
            <>
              <h1 className="font-heading text-3xl font-bold mb-2">Welcome Back</h1>
              <p className="text-[#A1A1AA] mb-8">Sign in to access your dubplates and orders</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input-dark w-full"
                    data-testid="login-email"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-sm text-lime hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="input-dark w-full pr-10"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="login-submit">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : 'Sign In'}
                </Button>
              </form>

              <p className="mt-8 text-center text-[#A1A1AA]">
                Don't have an account?{' '}
                <Link to="/register" className="text-lime hover:underline" data-testid="register-link">Sign up</Link>
              </p>
            </>
          ) : (
            <div data-testid="totp-step">
              <button
                type="button"
                onClick={handleBackToPassword}
                className="inline-flex items-center gap-1 text-sm text-[#A1A1AA] hover:text-white mb-6 transition-colors"
                data-testid="totp-back"
              >
                <ArrowLeft className="w-4 h-4" /> Use a different account
              </button>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-lime/10 mb-4">
                <ShieldCheck className="w-7 h-7 text-lime" />
              </div>
              <h1 className="font-heading text-3xl font-bold mb-2">Two-factor authentication</h1>
              <p className="text-[#A1A1AA] mb-8">
                Open your authenticator app and enter the 6-digit code for Tune4Tune. You can also paste a recovery code if you've lost access to your device.
              </p>

              <form onSubmit={handleTotpSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="totp-code">Authentication code</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="123 456"
                    className="input-dark w-full text-center text-2xl tracking-[0.4em] font-mono"
                    maxLength={20}
                    data-testid="totp-code-input"
                  />
                </div>
                <Button type="submit" disabled={totpLoading} className="w-full btn-primary" data-testid="totp-submit">
                  {totpLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : 'Verify & Sign in'}
                </Button>
              </form>
            </div>
          )}
        </motion.div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] to-transparent z-10" />
        <img src="https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg" alt="Studio" className="w-full h-full object-cover" />
      </div>
    </div>
  );
}
